import React, { useEffect, useRef, useState } from 'react';
import { Loader2, MonitorPlay, Music2, Pause, Play, Trash2, Upload, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND, THEMES, errMsg, kApi } from '../../lib/karaokeApi';
import { StageBackground } from './StageBackground';

const ThemeMusic = ({ theme, meta, onSaved }) => {
  const input = useRef(null);
  const [pct, setPct] = useState(null);
  const upload = async (file) => {
    if (!file) return;
    if (!/\.mp3$/i.test(file.name)) { toast.error('Please choose an MP3 file'); return; }
    if (file.size > 20 * 1024 * 1024) { toast.error('MP3 must be 20 MB or smaller'); return; }
    setPct(0);
    try { onSaved(await kApi.uploadMusic(theme.key, file, setPct)); toast.success(`Music saved for ${theme.label}`); } catch (e) { toast.error(errMsg(e, 'Upload failed')); } finally { setPct(null); if (input.current) input.current.value = ''; }
  };
  const remove = async () => {
    try { onSaved(await kApi.removeMusic(theme.key)); toast.success('Music removed'); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-2" data-testid={`karaoke-music-${theme.key}`}>
      <input ref={input} type="file" accept=".mp3,audio/mpeg" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`karaoke-music-input-${theme.key}`} />
      {meta ? (
        <>
          <p className="text-xs text-slate-600 break-words flex items-center gap-1"><Music2 className="w-3.5 h-3.5 shrink-0" /> {meta.filename} · {(meta.size / 1048576).toFixed(1)} MB</p>
          <audio controls preload="none" src={`${BACKEND}${meta.url}`} className="w-full h-9" data-testid={`karaoke-music-preview-${theme.key}`} />
        </>
      ) : <p className="text-xs text-slate-500">No music uploaded</p>}
      <div className="flex gap-2">
        <button type="button" onClick={() => input.current?.click()} disabled={pct !== null} className="flex-1 h-10 rounded-xl bg-slate-900 text-white text-sm font-semibold inline-flex items-center justify-center gap-1.5" data-testid={`karaoke-music-upload-${theme.key}`}>
          {pct !== null ? <><Loader2 className="w-4 h-4 animate-spin" /> {pct}%</> : <><Upload className="w-4 h-4" /> {meta ? 'Replace' : 'Upload MP3'}</>}
        </button>
        {meta && <button type="button" onClick={remove} className="h-10 px-3 rounded-xl bg-rose-50 text-rose-700" aria-label="Remove music" data-testid={`karaoke-music-remove-${theme.key}`}><Trash2 className="w-4 h-4" /></button>}
      </div>
    </div>
  );
};

// DJ theatre setup: theme, projector mode, background music. Saved as the hotel default.
export const TheatrePanel = ({ display, canControl, onChanged }) => {
  const [s, setS] = useState(null);
  const [vol, setVol] = useState(60);
  useEffect(() => { kApi.settings().then((d) => { setS(d); setVol(d.volume); }).catch(() => {}); }, []);
  if (!s) return <Loader2 className="w-6 h-6 animate-spin text-slate-400" />;
  const save = async (patch) => {
    try { const d = await kApi.saveSettings(patch); setS(d); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  const mode = display?.mode || 'between';
  const playing = !!display?.music_playing;
  return (
    <div className="space-y-6" data-testid="karaoke-theatre">
      <div className="flex flex-wrap gap-2 items-center">
        <button type="button" onClick={() => window.open('/karaoke/projector', 'ic-karaoke-projector')} className="h-11 px-4 rounded-xl bg-fuchsia-600 text-white font-semibold inline-flex items-center gap-2" data-testid="karaoke-open-projector">
          <MonitorPlay className="w-5 h-5" /> Open projector screen
        </button>
        {display && canControl && ['between', 'next'].map((m) => (
          <button key={m} type="button" disabled={mode === 'performance'} onClick={async () => { try { await kApi.setMode(m); onChanged(); } catch (e) { toast.error(errMsg(e)); } }}
            className={`h-11 px-4 rounded-xl font-semibold border ${mode === m ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-800 border-slate-200'} disabled:opacity-40`} data-testid={`karaoke-mode-${m}`}>
            {m === 'between' ? 'Between Songs' : 'Next Singer'}
          </button>
        ))}
        {mode === 'performance' && <span className="text-sm font-semibold text-fuchsia-700">Performance on screen</span>}
      </div>

      {canControl && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-4" data-testid="karaoke-music-controls">
          <button type="button" disabled={!display} onClick={() => save({ music_playing: !playing })} className={`h-11 px-4 rounded-xl font-semibold inline-flex items-center gap-2 ${playing ? 'bg-amber-500 text-white' : 'bg-emerald-600 text-white'} disabled:opacity-40`} data-testid="karaoke-music-toggle">
            {playing ? <><Pause className="w-4 h-4" /> Pause music</> : <><Play className="w-4 h-4" /> Play music</>}
          </button>
          <button type="button" onClick={() => save({ muted: !s.muted })} className="h-11 w-11 rounded-xl bg-slate-100 inline-flex items-center justify-center" aria-label={s.muted ? 'Unmute' : 'Mute'} data-testid="karaoke-music-mute">
            {s.muted ? <VolumeX className="w-5 h-5 text-rose-600" /> : <Volume2 className="w-5 h-5" />}
          </button>
          <label className="flex items-center gap-3 flex-1 min-w-[200px] text-sm text-slate-700">
            Volume
            <input type="range" min={0} max={100} value={vol} onChange={(e) => setVol(Number(e.target.value))} onPointerUp={() => save({ volume: vol })} onKeyUp={() => save({ volume: vol })} className="flex-1 accent-fuchsia-600" data-testid="karaoke-music-volume" />
            <span className="w-8 text-right font-semibold">{vol}</span>
          </label>
          {!display && <p className="text-xs text-slate-500 w-full">Start a session to play music on the projector.</p>}
          <p className="text-xs text-slate-500 w-full">Music plays on the projector between songs and pauses automatically during performances.</p>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-3">
        {THEMES.map((t) => (
          <div key={t.key} className={`rounded-2xl border-2 bg-white overflow-hidden ${s.theme === t.key ? 'border-fuchsia-500 shadow-lg' : 'border-slate-200'}`} data-testid={`karaoke-theme-${t.key}`}>
            <button type="button" disabled={!canControl} onClick={() => save({ theme: t.key })} className="relative block w-full aspect-video" aria-label={`Use ${t.label}`} data-testid={`karaoke-theme-select-${t.key}`}>
              <StageBackground theme={t.key} />
              <span className="absolute bottom-2 left-2 z-10 text-white font-bold text-sm drop-shadow">{t.label}{s.theme === t.key ? ' · Selected' : ''}</span>
            </button>
            <div className="p-3">{canControl ? <ThemeMusic theme={t} meta={s.music?.[t.key]} onSaved={setS} /> : <p className="text-xs text-slate-500">{s.music?.[t.key] ? 'Music uploaded' : 'No music'}</p>}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TheatrePanel;
